import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';

const FounderPage = () => {
  const focusAreas = [
    { title: 'Enterprise building', desc: 'Forming and developing companies around practical problems, proprietary capability and clearly defined markets.' },
    { title: 'Artificial intelligence and autonomy', desc: 'Developing systems that use artificial intelligence, computer vision, simulation and autonomous decision-making in operational environments.' },
    { title: 'Intellectual property', desc: 'Creating and protecting patents, trademarks, registered designs, software, data and technical know-how.' },
    { title: 'Property and strategic assets', desc: 'Building long-term interests in property, land and assets with practical or development value.' },
    { title: 'Stewardship', desc: 'Supporting animal welfare, responsible land use, education and long-term community benefit.' },
  ];

  return (
    <Layout>
      <SEO
        title="Calum Knox | Founder of Sandend House and The RLK Group"
        description="Calum Knox is a Scottish entrepreneur, founder and CEO of The RLK Group, and founder and principal of Sandend House."
        path="/founder"
        image="https://theknox.co.uk/og-founder.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "name": "Calum Knox",
          "url": "https://theknox.co.uk/founder",
          "mainEntity": {
            "@type": "Person",
            "@id": "https://theknox.co.uk/#founder",
            "name": "Calum Knox",
            "jobTitle": "Founder and Principal, Sandend House",
            "description": "Scottish entrepreneur, founder and CEO of The RLK Group, and founder and principal of Sandend House.",
            "worksFor": [
              { "@type": "Organization", "name": "Sandend House", "url": "https://theknox.co.uk" },
              { "@type": "Organization", "name": "The RLK Group", "url": "https://therlkgroup.com" }
            ]
          }
        }}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <p className="eyebrow mb-4">Founder and Principal</p>
              <h1 className="font-serif text-4xl lg:text-5xl mb-3" data-testid="founder-heading">Calum Knox</h1>
              <p className="text-[var(--sh-grey)] text-lg mb-8">
                Scottish entrepreneur, founder of The RLK Group and founder of Sandend House.
              </p>
              <p className="text-[var(--sh-charcoal)] leading-relaxed">
                Building businesses, protected technology and long-term assets through active enterprise.
              </p>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="aspect-[4/5] bg-[var(--sh-stone)] rounded overflow-hidden">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_aa62e94c-e559-4f19-a2b5-7166a5945181/artifacts/xnm4hccp_BFAA6454-E012-4E2A-A0D6-20883725C5C0.png"
                  alt="Calum Knox"
                  className="w-full h-full object-cover"
                  data-testid="founder-portrait"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-20 lg:py-28 bg-white border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] text-lg leading-relaxed mb-6">
              Calum Knox is a Scottish entrepreneur and the founder and CEO of The RLK Group, as well as the founder and principal of Sandend House.
            </p>
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-6">
              His work centres on the creation of businesses and protected technology across advanced artificial intelligence, autonomous systems, defence software, operational analytics and property technology.
            </p>
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-6">
              Calum is an active operator rather than a passive investor. He remains closely involved in strategy, capital allocation, product direction, commercial partnerships, governance and the formation of new ventures.
            </p>
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-6">
              Through The RLK Group, he has overseen the development of a protected intellectual-property portfolio covering autonomous guidance, coordinated systems, operational analytics and related technologies.
            </p>
            <p className="text-[var(--sh-charcoal)] leading-relaxed mb-6">
              He established Sandend House to place those commercial interests within a permanent family ownership structure, protect what has been built and create a disciplined platform for future investment in businesses, property, land and strategic assets.
            </p>
            <p className="text-[var(--sh-charcoal)] leading-relaxed">
              His operating philosophy is straightforward: identify substantive problems, build the capability required to solve them, retain meaningful control of the assets that matter and make decisions against a long-term horizon.
            </p>
          </div>
        </div>
      </section>

      {/* Areas of Focus */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <h2 className="font-serif text-3xl lg:text-4xl mb-12">Areas of focus</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {focusAreas.map((area, i) => (
              <div key={i} data-testid={`focus-${i}`}>
                <h3 className="font-serif text-lg mb-3 text-[var(--sh-ink)]">{area.title}</h3>
                <div className="w-8 h-px bg-[var(--sh-brass)] mb-3"></div>
                <p className="text-[var(--sh-grey)] text-sm leading-relaxed">{area.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speaking and Media */}
      <section className="py-20 lg:py-28 bg-white border-b border-[var(--sh-rule-light)]" data-testid="media-section">
        <div className="section-container">
          <p className="eyebrow mb-4">Speaking and Media</p>
          <h2 className="font-serif text-3xl lg:text-4xl mb-4">Selected appearances</h2>
          <p className="text-[var(--sh-grey)] mb-12 reading-width">
            Calum speaks at industry conferences and events on topics including artificial intelligence, autonomous systems, counter-drone technology, defence innovation and enterprise building.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-14">
            <div className="lg:col-span-7">
              <div className="aspect-[16/10] bg-[var(--sh-stone)] rounded overflow-hidden">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_aa62e94c-e559-4f19-a2b5-7166a5945181/artifacts/46v2bpvk_AE87E913-77A6-4B6D-B86D-261477A1093B.webp"
                  alt="Calum Knox speaking at the Counter UAS Homeland Security Europe conference, presenting RLK Technologies"
                  className="w-full h-full object-cover"
                  data-testid="media-conference-image"
                />
              </div>
              <p className="text-[var(--sh-grey)] text-xs mt-3">Counter UAS Homeland Security Europe — Calum Knox presenting on behalf of RLK Technologies</p>
            </div>
            <div className="lg:col-span-5">
              <div className="space-y-8">
                <div data-testid="appearance-0">
                  <p className="eyebrow-brass mb-2">Conference Speaker</p>
                  <h3 className="font-serif text-lg mb-1 text-[var(--sh-ink)]">Counter UAS Homeland Security Europe</h3>
                  <p className="text-[var(--sh-grey)] text-sm leading-relaxed">
                    Invited speaker presenting RLK Technologies' approach to counter-drone systems, autonomous detection and AI-driven threat classification.
                  </p>
                </div>
                <div className="border-t border-[var(--sh-rule-light)] pt-8" data-testid="appearance-1">
                  <p className="eyebrow-brass mb-2">Industry</p>
                  <h3 className="font-serif text-lg mb-1 text-[var(--sh-ink)]">Defence and security technology</h3>
                  <p className="text-[var(--sh-grey)] text-sm leading-relaxed">
                    Regular engagement with defence, security and technology communities on autonomous systems, AI capability and operational deployment.
                  </p>
                </div>
                <div className="border-t border-[var(--sh-rule-light)] pt-8" data-testid="appearance-2">
                  <p className="eyebrow-brass mb-2">Publication</p>
                  <h3 className="font-serif text-lg mb-1 text-[var(--sh-ink)]">Research and intellectual contribution</h3>
                  <p className="text-[var(--sh-grey)] text-sm leading-relaxed">
                    Published research and technical writing on autonomous systems, AI applications and related fields.
                  </p>
                  <Link to="/insights" className="text-[var(--sh-ink)] text-sm font-medium inline-flex items-center gap-1 mt-3 hover:text-[var(--sh-brass)] transition-colors">
                    View Insights <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Statement */}
      <section className="relative py-20 lg:py-28 bg-[var(--sh-ink)] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_aa62e94c-e559-4f19-a2b5-7166a5945181/artifacts/clr0k538_IMG_0578.jpeg"
            alt="Sandend Bay at sunset"
            className="w-full h-full object-cover opacity-15"
            data-testid="founder-statement-bg"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--sh-ink)] via-[var(--sh-ink)]/80 to-[var(--sh-ink)]/60"></div>
        </div>
        <div className="relative section-container">
          <p className="eyebrow-light mb-6">A note from the founder</p>
          <div className="max-w-3xl">
            <blockquote className="font-serif text-white/90 text-xl lg:text-2xl leading-relaxed italic mb-6">
              I did not establish Sandend House to make inherited wealth look more organised. I established it to build, protect and compound value through enterprise.
            </blockquote>
            <p className="text-white/55 leading-relaxed mb-4">
              Our starting point is not passive wealth management. It is the creation of useful businesses, defensible intellectual property and productive assets.
            </p>
            <p className="text-white/55 leading-relaxed mb-4">
              I believe ownership brings responsibility. Control should be retained where it matters, capital should be deployed patiently, and every business or asset should have a clear strategic purpose.
            </p>
            <p className="text-white/55 leading-relaxed mb-8">
              The objective is not to appear established. It is to become enduring.
            </p>
            <div className="border-t border-white/10 pt-6">
              <p className="text-white font-medium">Calum Knox</p>
              <p className="text-white/40 text-sm">Founder and Principal, Sandend House</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20">
        <div className="section-container flex flex-wrap gap-4">
          <Link to="/portfolio" className="btn-primary">
            Explore the Portfolio <ArrowRight size={16} className="ml-2" />
          </Link>
          <Link to="/the-house" className="btn-secondary">
            About Sandend House <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default FounderPage;
